import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, MagnifyingGlass, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const ALERT_STATUS = {
  expired: { label: 'Expired', class: 'bg-[#FEE2E2] text-[#991B1B]' },
  expiring_30: { label: 'Expiring in 30 days', class: 'bg-[#FEE2E2] text-[#991B1B]' },
  expiring_60: { label: 'Expiring in 60 days', class: 'bg-[#FEF9C3] text-[#854D0E]' },
  expiring_90: { label: 'Expiring in 90 days', class: 'bg-[#FEF9C3] text-[#854D0E]' },
  active: { label: 'Active', class: 'bg-[#DCFCE7] text-[#166534]' },
  archived: { label: 'Archived', class: 'bg-[#F1F5F9] text-[#475569]' }
};

const fmt = (n) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 0 }).format(n || 0);

export default function ContractsPage() {
  const { user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [alerts, setAlerts] = useState(null);

  const fetchContracts = () => {
    setLoading(true);
    const params = { limit: 100 };
    if (statusFilter !== 'all') params.status = statusFilter;
    if (search) params.search = search;
    api.get('/contracts', { params }).then(r => { setContracts(r.data.items); setTotal(r.data.total); }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchContracts(); }, [search, statusFilter]);
  useEffect(() => { api.get('/contracts/alerts').then(r => setAlerts(r.data)).catch(console.error); }, []);

  const alertTotal = alerts ? (alerts.expired?.length || 0) + (alerts.expiring_30?.length || 0) + (alerts.expiring_60?.length || 0) + (alerts.expiring_90?.length || 0) : 0;

  return (
    <div className="space-y-4" data-testid="contracts-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-bold tracking-tight">Contracts</h1>
        {user?.role === 'ap_accounting' && (
          <Link to="/contracts/create">
            <Button className="rounded-sm bg-[#0055FF] hover:bg-[#0043CC] gap-2" data-testid="add-contract-btn">
              <Plus size={16} /> New Contract
            </Button>
          </Link>
        )}
      </div>

      {/* Contract Alerts */}
      {alertTotal > 0 && (
        <Card className="rounded-sm border border-[#EF4444]/30 bg-[#FEE2E2]/10">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Warning size={16} className="text-[#EF4444]" /> Contract Alerts ({alertTotal})</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="text-center p-3 rounded-sm bg-[#FEE2E2]/30 border border-[#EF4444]/20">
                <p className="text-xl font-bold text-[#991B1B]">{alerts?.expired?.length || 0}</p>
                <p className="text-[10px] uppercase tracking-[0.1em] text-[#991B1B]">Expired</p>
              </div>
              <div className="text-center p-3 rounded-sm bg-[#FEE2E2]/20 border border-[#EF4444]/10">
                <p className="text-xl font-bold text-[#991B1B]">{alerts?.expiring_30?.length || 0}</p>
                <p className="text-[10px] uppercase tracking-[0.1em] text-[#991B1B]">30 Days</p>
              </div>
              <div className="text-center p-3 rounded-sm bg-[#FEF9C3]/30 border border-[#EAB308]/20">
                <p className="text-xl font-bold text-[#854D0E]">{alerts?.expiring_60?.length || 0}</p>
                <p className="text-[10px] uppercase tracking-[0.1em] text-[#854D0E]">60 Days</p>
              </div>
              <div className="text-center p-3 rounded-sm bg-[#FEF9C3]/20 border border-[#EAB308]/10">
                <p className="text-xl font-bold text-[#854D0E]">{alerts?.expiring_90?.length || 0}</p>
                <p className="text-[10px] uppercase tracking-[0.1em] text-[#854D0E]">90 Days</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search contracts..." className="pl-9 rounded-sm h-9" data-testid="contract-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] rounded-sm h-9" data-testid="contract-status-filter">
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent className="rounded-sm">
            <SelectItem value="all">All Contracts</SelectItem>
            <SelectItem value="expiring">Expiring Soon</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-[#475569]">{total} contracts</span>
      </div>

      <div className="border rounded-sm bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F1F5F9]">
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Code</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Title</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Vendor</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold text-right">Amount</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">End Date</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Recurring</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => <TableRow key={i}><TableCell colSpan={7}><div className="h-4 bg-muted rounded animate-pulse" /></TableCell></TableRow>)
            ) : contracts.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-[#94A3B8] py-8">No contracts found</TableCell></TableRow>
            ) : (
              contracts.map(c => {
                const s = ALERT_STATUS[c.computed_status] || ALERT_STATUS.active;
                return (
                  <TableRow key={c.id} className="hover:bg-[#F8FAFC]">
                    <TableCell className="text-sm font-mono">{c.contract_code}</TableCell>
                    <TableCell><Link to={`/contracts/${c.id}`} className="text-[#0055FF] hover:underline text-sm font-medium" data-testid={`contract-link-${c.contract_code}`}>{c.title}</Link></TableCell>
                    <TableCell className="text-sm">{c.vendor_name}</TableCell>
                    <TableCell className="text-right text-sm">{fmt(c.contract_amount)}</TableCell>
                    <TableCell className="text-sm">{c.end_date}</TableCell>
                    <TableCell><span className={`text-[10px] uppercase tracking-[0.05em] font-semibold px-1.5 py-0.5 rounded-sm ${s.class}`}>{s.label}</span></TableCell>
                    <TableCell>{c.is_recurring ? <span className="text-[10px] uppercase tracking-[0.05em] font-semibold px-1.5 py-0.5 rounded-sm bg-[#DCFCE7] text-[#166534]">{c.recurring_frequency}</span> : <span className="text-[#94A3B8] text-xs">-</span>}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
